/// <reference types="vite/client" />
import axios, { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store/authStore';
import { signalRefusal } from '@/lib/refusals';

// In production the SPA and the API share one origin (Caddy proxies /api to the
// backend service), so the relative default is correct there too. VITE_API_URL is
// only for pointing a local dev build at a non-proxied backend.
const API_BASE = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — attach access token
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Session refresh ────────────────────────────────────────────────────────────
//
// Refreshing used to treat EVERY failure as "your session is over": one 429 from
// the rate limiter, one dropped connection, or one 502 during a redeploy logged the
// user out and threw away whatever they were editing. Those are transient faults,
// not authentication decisions. Only the server saying 401/403 means the session is
// actually dead; everything else is retried and the session is left intact.
//
// Refresh tokens also ROTATE server-side, so two tabs refreshing at once would race:
// the first rotates the token, the second presents the now-stale one and gets logged
// out. A cross-tab lock plus token broadcast makes exactly one tab refresh, with the
// rest adopting its result.

/** The session is genuinely over — the only condition that justifies a logout. */
class SessionExpiredError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'SessionExpiredError';
  }
}

const REFRESH_LOCK = 'cognitest-auth-refresh';
const MAX_REFRESH_ATTEMPTS = 3;

// A refresh request with no timeout can hang indefinitely — a half-open connection
// on a flaky network never rejects on its own. Because the request runs while
// holding an exclusive cross-tab lock, one stalled tab would block every other tab's
// refresh forever. Both the request and the lock therefore need a hard ceiling.
const REFRESH_TIMEOUT_MS = 15_000;
const LOCK_TIMEOUT_MS = 45_000;

const channel =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('cognitest-auth') : null;

// Adopt whatever a sibling tab worked out, rather than duplicating the work.
channel?.addEventListener('message', (event) => {
  const data = event.data as { type?: string; token?: string } | null;
  if (data?.type === 'token' && data.token) {
    useAuthStore.getState().setAccessToken(data.token);
  } else if (data?.type === 'logout') {
    useAuthStore.getState().logout();
  }
});

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Backoff for a transient failure, honouring Retry-After when the server sends one. */
function backoffFor(error: unknown, attempt: number): number {
  const header = (error as AxiosError)?.response?.headers?.['retry-after'];
  const retryAfter = Number(header);
  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    return Math.min(retryAfter * 1000, 30_000);
  }
  return 500 * 2 ** attempt;
}

/** POST /auth/refresh, retrying transient failures. Throws SessionExpiredError only on 401/403. */
async function postRefresh(): Promise<string> {
  let lastError: unknown;

  for (let attempt = 0; attempt < MAX_REFRESH_ATTEMPTS; attempt++) {
    try {
      const { data } = await axios.post(
        `${API_BASE}/auth/refresh`,
        {},
        { withCredentials: true, timeout: REFRESH_TIMEOUT_MS },
      );
      return data.data.accessToken as string;
    } catch (error) {
      lastError = error;
      const status = (error as AxiosError).response?.status;

      // The server has judged this session invalid. Retrying cannot help.
      if (status === 401 || status === 403) throw new SessionExpiredError();

      // 429, 5xx, or no response at all: transient. Wait and try again.
      if (attempt < MAX_REFRESH_ATTEMPTS - 1) await sleep(backoffFor(error, attempt));
    }
  }

  // Out of attempts, but never proven unauthenticated — keep the session and let the
  // caller surface the failure. The next user action will try again.
  throw lastError;
}

let inFlight: Promise<string> | null = null;

/** Single-flight refresh, coordinated within this tab and across tabs. */
function refreshAccessToken(): Promise<string> {
  if (inFlight) return inFlight;

  const run = async (): Promise<string> => {
    const tokenBefore = useAuthStore.getState().accessToken;

    const doRefresh = async (): Promise<string> => {
      // Inside the lock another tab may already have refreshed and broadcast a new
      // token. If the store moved on, that token is good — don't rotate again.
      const current = useAuthStore.getState().accessToken;
      if (current && current !== tokenBefore) return current;

      const token = await postRefresh();
      useAuthStore.getState().setAccessToken(token);
      channel?.postMessage({ type: 'token', token });
      return token;
    };

    // Web Locks gives a real cross-tab mutex. Where it's unavailable, fall back to
    // the in-tab single-flight above, which still prevents the common case.
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      // Bounded wait. An exclusive lock queues indefinitely by default, so without
      // this a tab that acquired the lock and then froze (suspended laptop, wedged
      // connection) would stall every other tab's refresh for as long as it lived.
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), LOCK_TIMEOUT_MS);
      try {
        return await navigator.locks.request(
          REFRESH_LOCK,
          { signal: abort.signal },
          doRefresh,
        );
      } catch (err) {
        // AbortError means we never got the lock in time — go ahead unlocked rather
        // than leaving the user stuck. Worst case is a redundant rotation.
        if ((err as Error)?.name !== 'AbortError') throw err;
        return doRefresh();
      } finally {
        clearTimeout(timer);
      }
    }
    return doRefresh();
  };

  inFlight = run().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

/**
 * Surface a transient connectivity problem once, not once per failed request.
 *
 * A dashboard can have several queries in flight, and every one of them would
 * otherwise raise its own toast for the same underlying outage.
 */
let lastTroubleToastAt = 0;
function notifyConnectionTrouble(): void {
  const now = Date.now();
  if (now - lastTroubleToastAt < 15_000) return;
  lastTroubleToastAt = now;
  toast.error("Can't reach the server — you're still signed in. Retrying shortly.", {
    id: 'connection-trouble',
  });
}

/**
 * Tell sibling tabs the session is over.
 *
 * Exported so an explicit sign-out can use it too. Without this, signing out in one
 * tab left every other tab rendering the full authenticated UI — the store is
 * per-tab, and only the cookie is shared — so the app looked signed in until the
 * next request failed.
 */
export function broadcastLogout(): void {
  channel?.postMessage({ type: 'logout' });
}

/** End the session everywhere and send the user to login, preserving where they were. */
function endSession(): void {
  useAuthStore.getState().logout();
  broadcastLogout();

  const here = window.location.pathname + window.location.search;
  const isAuthPage = window.location.pathname.startsWith('/auth/');
  const next = isAuthPage ? '' : `?next=${encodeURIComponent(here)}`;
  window.location.href = `/auth/login${next}`;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't run the session-refresh flow for the auth endpoints themselves — a failed
    // login/refresh returns 401 by design and should surface its own error, not trigger
    // a spurious refresh + redirect.
    const url: string = originalRequest?.url ?? '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Only a definitive 401/403 from the server ends the session. A rate limit,
        // an outage, or a flaky connection leaves the user signed in.
        if (refreshError instanceof SessionExpiredError) {
          endSession();
        } else {
          // Keeping the session is right, but staying silent is not: the request
          // still failed, and without a word the page just looks broken. Say what
          // happened and make clear the session is intact.
          notifyConnectionTrouble();
        }
        return Promise.reject(refreshError);
      }
    }

    // A refusal can mean the role stored at sign-in is out of date (a demotion, or an
    // account the server now fences). Say so; with "Refresh sign-in" on, the session keeper
    // reads /auth/me again. Never for /auth/* itself: a refused /auth/me would only ask
    // again for the same answer.
    const status = error.response?.status;
    if ((status === 403 || status === 404) && !url.includes('/auth/')) signalRefusal();

    return Promise.reject(error);
  },
);

export default api;
